import React, { useEffect } from "react";
import Table from "react-bootstrap/Table";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import moment from "moment-timezone";
import lodash from "lodash";
import useStyles from "../dashAuction.style";

// mirrors the lot status shown on the edit lots screen
const getLotStatus = (lot, eventFormat) => {
  if (moment().isBefore(moment(lot.startDateAndTime))) return "Upcoming";

  const isLive =
    eventFormat === "ENGLISH_AUCTION"
      ? moment().isBetween(
          moment(lot.startDateAndTime),
          moment(lot.endDateAndTime),
          null,
          "[]"
        )
      : lot.concluded === "NO";

  return isLive ? "Live" : "Ended";
};

// rendered by the auctions table when a row is expanded - lists the lots of
// that auction. lots are fetched lazily (only for the rows on the current
// page) and cached by the table, so collapsing and expanding again is free
function AuctionLotsRow({ data, lotsByEvent, fetchLots, getZone }) {
  const classes = useStyles();
  const { eventId, eventFormatRaw, eventType } = data;
  const lots = lotsByEvent[eventId];

  useEffect(() => {
    if (!lots) fetchLots(eventId);
  }, [eventId, lots, fetchLots]);

  const formatDateTime = (value) =>
    value ? moment(value).tz(getZone).format("DD/MM/YYYY hh:mm:ss") : "-";

  const formatAmount = (value, currency) =>
    value === null || value === undefined || value === ""
      ? "-"
      : `${value} ${currency || ""}`.trim();

  const statusClass = {
    Live: classes.lotStatusLive,
    Upcoming: classes.lotStatusUpcoming,
    Ended: classes.lotStatusEnded,
  };

  if (!lots || lots.apiState === "loading") {
    return (
      <div className={classes.expandedLots}>
        <span className={classes.lotsMessage}>
          <CircularProgress size={14} /> Loading lots...
        </span>
      </div>
    );
  }

  if (lots.apiState === "error") {
    return (
      <div className={classes.expandedLots}>
        <span className={classes.lotsMessage}>
          Lots could not be loaded.
          <Button size="small" onClick={() => fetchLots(eventId, true)}>
            Retry
          </Button>
        </span>
      </div>
    );
  }

  if (lodash.isEmpty(lots.list)) {
    return (
      <div className={classes.expandedLots}>
        <span className={classes.lotsMessage}>
          No lots have been added to this auction yet.
        </span>
      </div>
    );
  }

  const isEnglishAuction = eventFormatRaw === "ENGLISH_AUCTION";

  return (
    <div className={classes.expandedLots}>
      <div className={classes.lotsHeading}>
        Lots ({lots.list.length}) &middot; {data.auctionTitle}
      </div>
      <Table size="sm" bordered hover responsive className={classes.lotsTable}>
        <thead>
          <tr>
            <th>Lot No.</th>
            <th>Item No.</th>
            <th>Status</th>
            <th>Short Description</th>
            <th>Qty</th>
            <th>Unit</th>
            <th>Start Price</th>
            <th>{eventType === "REVERSE" ? "Decrement" : "Increment"}</th>
            <th>Reserve Price</th>
            <th>Current Price</th>
            <th>Start Date &amp; Time</th>
            <th>{isEnglishAuction ? "End Date & Time" : "Time Interval"}</th>
          </tr>
        </thead>
        <tbody>
          {lots.list.map((lot) => {
            const status = getLotStatus(lot, eventFormatRaw);

            return (
              <tr key={lot.eventItemId}>
                <td>{lot.lotNumber}</td>
                <td>{lot.itemNumber || "-"}</td>
                <td>
                  <span className={statusClass[status]}>{status}</span>
                </td>
                <td>{lot.shortDescription}</td>
                <td>{lot.quantity}</td>
                <td>{lot.unit}</td>
                <td>{formatAmount(lot.startPrice, lot.currency)}</td>
                <td>{formatAmount(lot.increment, lot.currency)}</td>
                <td>{formatAmount(lot.reservePrice, lot.currency)}</td>
                <td>{formatAmount(lot.currentPrice, lot.currency)}</td>
                <td>{formatDateTime(lot.startDateAndTime)}</td>
                <td>
                  {isEnglishAuction
                    ? formatDateTime(lot.endDateAndTime)
                    : lot.timeInterval
                      ? `${lot.timeInterval} min.`
                      : "-"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}

export default AuctionLotsRow;
